import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ChevronDown } from 'lucide-react'
import { SPACE_CATEGORIES, type DbUnitType } from '@/lib/reports/space-yield/types'
import { formatUnitType } from '@/lib/reports/space-yield/compute'

interface SpaceTypeTogglesProps {
  unitTypeCounts: Record<string, number>
  types: DbUnitType[]
  onTypesChange: (types: DbUnitType[]) => void
}

export function SpaceTypeToggles({ unitTypeCounts, types, onTypesChange }: SpaceTypeTogglesProps) {
  const typesSet = new Set(types)

  // Only show categories that have at least 1 unit at this location
  const visibleCategories = SPACE_CATEGORIES.filter((cat) =>
    cat.types.some((t) => (unitTypeCounts[t] || 0) > 0)
  )

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500 font-medium hidden lg:inline">Space:</span>
      <div className="flex gap-1">
        {visibleCategories.map((cat) => {
          // Only subtypes that actually exist at this location
          const presentTypes = cat.types.filter((t) => (unitTypeCounts[t] || 0) > 0)
          const selectedCount = presentTypes.filter((t) => typesSet.has(t)).length
          const allSelected = selectedCount === presentTypes.length
          const someSelected = selectedCount > 0
          const isActive = someSelected

          // Simple toggle: exactly 1 subtype present
          if (presentTypes.length === 1) {
            const singleType = presentTypes[0]
            const isOn = typesSet.has(singleType)
            return (
              <Badge
                key={cat.id}
                variant={isOn ? 'default' : 'outline'}
                className={`cursor-pointer ${
                  isOn
                    ? 'bg-slate-900'
                    : cat.defaultOn
                      ? 'text-slate-500 hover:text-slate-900'
                      : 'text-slate-400 hover:text-slate-600 border-dashed'
                }`}
                onClick={() => {
                  if (isOn) {
                    // Don't allow deselecting the last type
                    if (types.length <= 1) return
                    onTypesChange(types.filter((t) => t !== singleType))
                  } else {
                    onTypesChange([...types, singleType])
                  }
                }}
              >
                {cat.label}
              </Badge>
            )
          }

          // Expandable toggle: >=2 subtypes present
          return (
            <Popover key={cat.id}>
              <PopoverTrigger asChild>
                <Badge
                  variant={isActive ? 'default' : 'outline'}
                  className={`cursor-pointer gap-1 ${
                    isActive
                      ? allSelected
                        ? 'bg-slate-900'
                        : 'bg-slate-700'
                      : cat.defaultOn
                        ? 'text-slate-500 hover:text-slate-900'
                        : 'text-slate-400 hover:text-slate-600 border-dashed'
                  }`}
                >
                  {cat.label}
                  {someSelected && !allSelected && (
                    <span className="text-[10px] opacity-70">
                      {selectedCount}/{presentTypes.length}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3 ml-0.5" />
                </Badge>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <div className="space-y-2">
                  {/* Select all / none toggle */}
                  <div
                    className="flex items-center gap-2 pb-2 border-b cursor-pointer"
                    onClick={() => {
                      if (allSelected) {
                        // Deselect all subtypes of this category (but keep at least 1 type overall)
                        const remaining = types.filter((t) => !presentTypes.includes(t))
                        if (remaining.length > 0) {
                          onTypesChange(remaining)
                        }
                      } else {
                        // Select all subtypes
                        const newTypes = new Set(types)
                        for (const t of presentTypes) newTypes.add(t)
                        onTypesChange(Array.from(newTypes))
                      }
                    }}
                  >
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      className="pointer-events-none"
                    />
                    <span className="text-sm font-medium">All {cat.label}</span>
                  </div>

                  {/* Individual subtypes */}
                  {presentTypes.map((subtype) => {
                    const count = unitTypeCounts[subtype] || 0
                    const isChecked = typesSet.has(subtype)
                    return (
                      <div
                        key={subtype}
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => {
                          if (isChecked) {
                            if (types.length <= 1) return
                            onTypesChange(types.filter((t) => t !== subtype))
                          } else {
                            onTypesChange([...types, subtype])
                          }
                        }}
                      >
                        <Checkbox checked={isChecked} className="pointer-events-none" />
                        <span className="text-sm flex-1">{formatUnitType(subtype)}</span>
                        <span className="text-xs text-slate-400">{count}</span>
                      </div>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )
        })}
      </div>
    </div>
  )
}
